import monitor from "csi/monitor/_base";
import { resolveNodes } from "csi/service/nodeService";
import { ReactionStore } from "csi/utility/mobxUtility";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import xml from "csi/xml";
import _ from "lodash";
import { action, makeObservable, observable } from "mobx";
import { createContext, MutableRefObject, useContext } from "react";
import { manageProofLock, TASK_STATUS_ICONS } from "../ProofService";

/** One decision can cause many events and even lock the proof. Debouncing for 1.5 seconds allows all the updates to */
const DEBOUNCE_PROOF_MONITOR_TIME = 1500;

export class ManageProofStore extends ReactionStore {
	@observable dojoViewDimensions: DOMRect = null;
	@observable dojoViewKey = Date.now();
	@observable hasProofBeenUpdatedByAnotherUser = false;
	@observable isDojoViewLoading = false;
	@observable isManageProofPopupOpen = false;
	@observable isProofLockLoading = false;
	@observable isProofLocked = false;
	@observable isUserProofAdmin = false;
	@observable proofDocumentName = "";
	@observable proofStatus = "";

	readonly cache: ICache = new WorkingCache();

	documentRevisionCnl: cnl = null;
	hasProofBeenUpdatedByAnotherUserHandle: dojo.Handle = null;
	hasProofStageBeenUpdatedByAnotherUserHandle: Record<string, dojo.Handle> = {};
	hasProofTaskBeenUpdatedByAnotherUserHandle: Record<string, dojo.Handle> = {};
	isLegacyProof = false;
	proofUrl = "";
	proofWorkflowInstanceCnl: cnl = null;
	stagesByName: Record<string, C8Node> = {};
	tasksByCnl: Record<cnl, C8Node> = {};

	get documentRevision(): C8Node {
		return this.cache.get(this.documentRevisionCnl);
	}

	get proofWorkflowInstance(): C8Node {
		return this.cache.get(this.proofWorkflowInstanceCnl);
	}

	/** indicate whether or not show the skeleton for the dojo view container */
	get showDojoViewSkeleton(): boolean {
		return this.isLoading && !!this.dojoViewDimensions;
	}

	get showManageProofButton(): boolean {
		return !this.isLegacyProof && this.isUserProofAdmin;
	}

	get stageNodes(): C8Node[] {
		return this.cache.getNodes(this.proofWorkflowInstance.Subprocesses);
	}

	get isLoading(): boolean {
		return this.isDojoViewLoading || this.isProofLockLoading;
	}

	get taskStatusColor(): string {
		return TASK_STATUS_ICONS[xml.enumGetText(this.proofStatus)].color;
	}

	get taskStatusIcon(): string {
		return TASK_STATUS_ICONS[xml.enumGetText(this.proofStatus)].icon;
	}

	openManageProofPopup = (): void => {
		this.setIsManageProofPopupOpen(true);
	};

	closeManageProofPopup = (): void => {
		this.setIsManageProofPopupOpen(false);
	};

	@action setDojoViewDimensions = (dimensions: DOMRect): void => {
		this.dojoViewDimensions = dimensions;
	};

	@action setHasProofBeenUpdatedByAnotherUser = (updated: boolean): void => {
		this.hasProofBeenUpdatedByAnotherUser = updated;
	};

	@action setIsProofLockLoading = (loading: boolean): void => {
		this.isProofLockLoading = loading;
	};

	@action setIsDojoViewLoading = (loading: boolean): void => {
		this.isDojoViewLoading = loading;
	};

	@action setDojoViewKey = (): void => {
		this.dojoViewKey = Date.now();
	};

	@action setProofDocumentName = (name: string): void => {
		this.proofDocumentName = name;
	};

	@action setProofStatus = (status: string): void => {
		this.proofStatus = status;
	};

	@action setIsManageProofPopupOpen = (open: boolean): void => {
		this.isManageProofPopupOpen = open;
	};

	@action setIsProofLocked = (lock: boolean): void => {
		this.isProofLocked = lock;
	};

	@action setIsUserProofAdmin = (admin: boolean): void => {
		this.isUserProofAdmin = admin;
	};

	/** update the proof and close the updated warning dialog */
	updateLocalProofState = async (): Promise<void> => {
		// we are updating the revision state, so the proof has no longer been updated by another user
		this.setHasProofBeenUpdatedByAnotherUser(false);

		// get the latest version of the nodes
		const latestNodes = await resolveNodes([
			this.documentRevisionCnl,
			this.proofWorkflowInstanceCnl,
			...this.proofWorkflowInstance.Subprocesses,
		]);
		latestNodes.forEach((node) => this.cache.add(node));

		// update lock status
		if (this.documentRevision.ProofIsLocked !== this.isProofLocked) {
			this.setIsProofLocked(this.documentRevision.ProofIsLocked);
		}

		// update proof decision status
		if (this.documentRevision.ProofStatus !== this.proofStatus) {
			this.setProofStatus(this.documentRevision.ProofStatus);
		}

		// update local state
		for (const node of this.stageNodes) {
			this.stagesByName[xml.nodeGetName(node)] = node;
		}

		// update stage decision and lock status' by changing the key on the dojo view
		this.setDojoViewKey();
	};

	/** if there is a proof change node handle, remove it */
	cleanupProofChangeNodeHandles = (): void => {
		this.hasProofBeenUpdatedByAnotherUserHandle?.remove();
		this.hasProofBeenUpdatedByAnotherUserHandle = null;
	};

	/** remove all proof stage change node handles */
	cleanupProofStageChangeNodeHandles = (): void => {
		for (const stageName of Object.keys(this.stagesByName)) {
			this.hasProofStageBeenUpdatedByAnotherUserHandle[stageName]?.remove();
			delete this.hasProofStageBeenUpdatedByAnotherUserHandle[stageName];
		}
	};

	/** remove all proof task change node handles */
	cleanupProofTaskChangeNodeHandles = (): void => {
		for (const taskCnl of Object.keys(this.tasksByCnl)) {
			this.hasProofTaskBeenUpdatedByAnotherUserHandle[taskCnl]?.remove();
			delete this.hasProofTaskBeenUpdatedByAnotherUserHandle[taskCnl];
		}
	};

	/**
	 * Update proof by setting loading screens and ending the action.
	 * TODO: We might later use this node that comes from monitor.onChangeNode to update each piece.
	 */
	updateProof = (node: C8Node): void => {
		if (!this.isManageProofPopupOpen) {
			return;
		}

		this.setIsProofLockLoading(true);
		this.setIsDojoViewLoading(true);
		this.endProofAction();
	};

	debounced_updateProof = _.debounce(this.updateProof, DEBOUNCE_PROOF_MONITOR_TIME);

	/** initialize the node monitors, adding all necessary cleanup */
	initializeNodeMonitors = (): void => {
		// listen for changes to proof
		this.hasProofBeenUpdatedByAnotherUserHandle = monitor.onChangeNode(
			this.documentRevision.$URL,
			(documentRevision) => {
				if (this.isDifferenceInCompareProofDocumentRevisionNodes(documentRevision, this.documentRevision)) {
					this.debounced_updateProof(documentRevision);
				}
			},
		);

		// listen for changes to proof stages
		const stageNodes = this.cache.getNodes(this.proofWorkflowInstance.Subprocesses);
		for (const stageNode of stageNodes) {
			const nodeName = xml.nodeGetName(stageNode);
			this.hasProofStageBeenUpdatedByAnotherUserHandle[nodeName] = monitor.onChangeNode(
				stageNode.$URL,
				(stageNodeFromMonitor) => {
					if (this.isDifferenceInCompareProofStageNodes(stageNodeFromMonitor, this.stagesByName[xml.nodeGetName(stageNodeFromMonitor)])) {
						this.debounced_updateProof(stageNodeFromMonitor);
					}
				},
			);
			this.stagesByName[nodeName] = stageNode;

			// listen for changes to proof tasks
			const taskNodes = this.cache.getNodes(stageNode.Tasks);
			for (const taskNode of taskNodes) {
				const taskNodeCnl = taskNode.$URL;
				this.hasProofTaskBeenUpdatedByAnotherUserHandle[taskNodeCnl] = monitor.onChangeNode(
					taskNode.$URL,
					(taskNodeFromMonitor) => {
						if (this.isDifferenceInCompareProofTaskNodes(taskNodeFromMonitor, this.tasksByCnl[taskNodeCnl])) {
							this.debounced_updateProof(taskNodeFromMonitor);
						}
					},
				);
				this.tasksByCnl[taskNodeCnl] = taskNode;
			}
		}

		// when leaving the page, make sure to clean up the change node monitor
		this.trackReaction(this.cleanupProofChangeNodeHandles);
		this.trackReaction(this.cleanupProofStageChangeNodeHandles);
		this.trackReaction(this.cleanupProofTaskChangeNodeHandles);
	};

	/** compare only the differences that matter in the proof node */
	isDifferenceInCompareProofDocumentRevisionNodes = (a: C8Node, b: C8Node): boolean => {
		// compare proof lock
		if (a.ProofIsLocked !== b.ProofIsLocked) {
			return true;
		}

		// compare proof status
		if (a.ProofStatus !== b.ProofStatus) {
			return true;
		}

		return false;
	};

	/** compare only the differences that matter in the proof stage node */
	isDifferenceInCompareProofStageNodes = (a: C8Node, b: C8Node): boolean => {
		// compare stage lock
		if (a.StageIsLocked !== b.StageIsLocked) {
			return true;
		}

		// compare stage decision status
		if (a.DecisionStatus !== b.DecisionStatus) {
			return true;
		}

		return false;
	};

	/** compare only the differences that matter in the proof task node */
	isDifferenceInCompareProofTaskNodes = (a: C8Node, b: C8Node): boolean => {
		// compare stage decision status
		if (a.DecisionStatus !== b.DecisionStatus) {
			return true;
		}

		return false;
	};

	/** end the proof lock event and resume updates */
	endProofAction = async (): Promise<void> => {
		await this.updateLocalProofState();
		this.setIsProofLockLoading(false);
	};

	/** begin the proof lock event and watch for the proof to be updated, block updates */
	beginProofLockAction = (): void => {
		this.setIsDojoViewLoading(true);
		this.setIsProofLockLoading(true);

		const lock = !this.isProofLocked;

		manageProofLock(lock, this.proofWorkflowInstance.$URL, this.documentRevision.$URL);
		this.setIsProofLocked(lock);
	};

	/** begin the reviewer decision event and block updates */
	beginProofTaskAction = (): void => {
		this.setIsDojoViewLoading(true);
		this.setIsProofLockLoading(true);
	};

	/** view done loading event for dojo view within manage proof dialog */
	onViewLoad = (ref: MutableRefObject<HTMLElement>): void => {
		if (!this.dojoViewDimensions) {
			this.setDojoViewDimensions(ref.current.getBoundingClientRect());
		}
		this.setIsDojoViewLoading(false);
	};

	constructor(
		cache: ICache,
		documentRevisionCnl: cnl,
		proofWorkflowInstanceCnl: cnl,
		proofDocumentName: string,
		proofUrl: string,
		isUserProofAdmin: boolean,
		isLegacyProof: boolean,
	) {
		super();
		makeObservable(this);

		this.cache = cache;
		this.documentRevisionCnl = documentRevisionCnl;
		this.proofWorkflowInstanceCnl = proofWorkflowInstanceCnl;
		this.proofDocumentName = proofDocumentName;
		this.proofStatus = this.documentRevision.ProofStatus;
		this.isProofLocked = this.documentRevision.ProofIsLocked;
		this.isUserProofAdmin = isUserProofAdmin;
		this.isLegacyProof = isLegacyProof;
		this.proofUrl = proofUrl;
		this.initializeNodeMonitors();
	}
}

export const ManageProofStoreContext = createContext<ManageProofStore>(undefined);
export const useManageProofStore = (): ManageProofStore => useContext(ManageProofStoreContext);